import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { updatePrefsRequest } from '../../redux/prefs/prefsSlice';
import { LOCALE_LABELS } from '../../constants/locales';
import { RTL_DIRECTION, currencies } from '../../constants/default';
import { trackEvent } from '../../utils/analytics';
import './styles.scss';

// Mapping from country code to likely language
const countryToLanguage = {
  'US': 'en',
  'GB': 'en',
  'CA': 'en',
  'ES': 'es',
  'FR': 'fr',
  'DE': 'de',
  'IT': 'it',
  'PL': 'pl',
  'RU': 'ru',
  'AR': 'es', // Argentina
  'BR': 'pt-BR',
  'PT': 'pt-PT',
  'IL': 'he',
  'SA': 'ar', // Saudi Arabia
  'AU': 'en', // Australia
  // Add more mappings as needed
};

// Mapping from country code to commonly used currency (if supported)
const countryToCurrency = {
  'US': 'USD',
  'GB': 'GBP',
  'CA': 'USD', // CAD not supported, fallback to USD
  'ES': 'EUR',
  'FR': 'EUR',
  'DE': 'EUR',
  'IT': 'EUR',
  'PL': 'EUR', // PLN not supported, fallback to EUR
  'RU': 'EUR', // RUB not supported, fallback to EUR
  'AR': 'USD', // ARS not supported, fallback to USD
  'BR': 'USD', // BRL not supported, fallback to USD
  'PT': 'EUR',
  'IL': 'ILS',
  'SA': 'EUR', // SAR not supported, fallback to EUR
  'AU': 'AUD',
  // Add more mappings as needed
};

const LanguageSuggestionBanner = () => {
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const { residency = null, langManuallySet = false } = useSelector(state => state.prefs);
  const [dismissed, setDismissed] = useState(false);
  const [suggestedTranslations, setSuggestedTranslations] = useState(null);
  const [currentTranslations, setCurrentTranslations] = useState(null);

  const currentLang = i18n.language;
  const suggestedLang = residency ? countryToLanguage[residency] : null;
  const shouldShow = suggestedLang && suggestedLang !== currentLang && !dismissed && !langManuallySet && suggestedTranslations && currentTranslations;

  useEffect(() => {
    // Reset dismissed if residency or currentLang changes
    setDismissed(false);
    setSuggestedTranslations(null);
    // Note: currentTranslations is not reset here because it depends on currentLang, not residency
  }, [residency, currentLang]);

  useEffect(() => {
    if (suggestedLang) {
      i18n.loadLanguages(suggestedLang).then(() => {
        const fixedTSuggested = i18n.getFixedT(suggestedLang);
        const suggestedLangName = LOCALE_LABELS[suggestedLang] || suggestedLang;
        setSuggestedTranslations({
          message: fixedTSuggested('languageSuggestion.message', { language: suggestedLangName }),
          switchButton: fixedTSuggested('languageSuggestion.switch', { language: suggestedLangName })
        });
      });
    }
  }, [suggestedLang, i18n]);

  useEffect(() => {
    if (currentLang) {
      const fixedTCurrent = i18n.getFixedT(currentLang);
      const currentLangName = LOCALE_LABELS[currentLang] || currentLang;
      setCurrentTranslations({
        keepButton: fixedTCurrent('languageSuggestion.keep', { language: currentLangName })
      });
    }
  }, [currentLang, i18n]);

  useEffect(() => {
    if (shouldShow) {
      trackEvent('Language Suggestion Banner Shown', {
        event_category: 'Language Suggestion',
        event_label: `${currentLang} -> ${suggestedLang} (residency: ${residency})`
      });
    }
  }, [shouldShow, currentLang, suggestedLang, residency]);

  const handleSwitch = () => {
    trackEvent('Language Suggestion Switched', {
      event_category: 'Language Suggestion',
      event_label: `${currentLang} -> ${suggestedLang} (residency: ${residency})`
    });
    const suggestedCurrency = residency ? countryToCurrency[residency] : null;
    const payload = { lang: suggestedLang, langManuallySet: true };
    if (suggestedCurrency && currencies.includes(suggestedCurrency)) {
      payload.cur = suggestedCurrency;
    }
    dispatch(updatePrefsRequest(payload));
    setDismissed(true);
  };

  const handleKeep = () => {
    trackEvent('Language Suggestion Kept', {
      event_category: 'Language Suggestion',
      event_label: `${currentLang} (suggested: ${suggestedLang}, residency: ${residency})`
    });
    dispatch(updatePrefsRequest({ langManuallySet: true }));
    setDismissed(true);
  };

  // Translations are loaded asynchronously
  const message = suggestedTranslations?.message;
  const switchButtonText = suggestedTranslations?.switchButton;
  const keepButtonText = currentTranslations?.keepButton;
  const bannerDirection = RTL_DIRECTION[suggestedLang] ? 'rtl' : 'ltr';


  return (
    <div className={`language-suggestion-banner ${shouldShow ? 'visible' : ''}`} dir={bannerDirection}>
      <div className="container">
        <div className="banner-content">
          <span className="message">{message}</span>
          <div className="buttons">
            <button className="btn btn-primary btn-sm" onClick={handleSwitch}>
              {switchButtonText}
            </button>
            <button className="btn btn-outline-secondary btn-sm" onClick={handleKeep}>
              {keepButtonText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LanguageSuggestionBanner;